import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NearestBillboard } from '@/features/billboards/api/billboardApi';
import { getGoogleMapsApiKey, formatScreenName } from '@/features/billboards/utils/billboardDetailUtils';
import {
  createScreenistaMarkerIcon,
  getBillboardCoordinates,
  getMarkerIconConfig,
  loadGoogleMapsScript,
} from '@/shared/lib/maps/googleMaps';

type NearestScreenLocationsProps = {
  locations: NearestBillboard[];
  /** Base path for marker clicks — `/screens` keeps AppLayout sidebar. */
  detailBasePath?: '/screens' | '/billboard';
  /** Preserve origin so Back on the next detail page returns correctly. */
  backTo?: string;
};

const DEFAULT_CENTER = { lat: 28.7041, lng: 77.1025 };

export function NearestScreenLocations({
  locations,
  detailBasePath = '/billboard',
  backTo,
}: NearestScreenLocationsProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [mapError, setMapError] = useState(false);

  const validLocations = useMemo(
    () =>
      locations.filter((location) => Boolean(getBillboardCoordinates(location))),
    [locations],
  );

  useEffect(() => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      setMapError(true);
      return;
    }

    let markers: google.maps.Marker[] = [];
    let isMounted = true;

    loadGoogleMapsScript(apiKey)
      .then(async (googleMaps) => {
        if (!mapRef.current || !isMounted) return;

        const markerIconUrl = await createScreenistaMarkerIcon();
        const first = validLocations[0]
          ? getBillboardCoordinates(validLocations[0])
          : null;

        const map = new googleMaps.Map(mapRef.current, {
          center: first || DEFAULT_CENTER,
          zoom: validLocations.length > 1 ? 10 : 14,
          mapTypeId: 'roadmap',
          gestureHandling: 'cooperative',
        });

        const bounds = new googleMaps.LatLngBounds();

        validLocations.forEach((location) => {
          const position = getBillboardCoordinates(location);
          if (!position) return;

          const marker = new googleMaps.Marker({
            position,
            map,
            title: formatScreenName(location.screenName, 'Screen'),
            icon: getMarkerIconConfig(googleMaps, markerIconUrl),
          });

          marker.addListener('click', () => {
            if (!location._id) return;
            navigate(`${detailBasePath}/${location._id}`, {
              state: backTo ? { from: backTo } : undefined,
            });
          });

          markers.push(marker);
          bounds.extend(position);
        });

        if (markers.length > 1) map.fitBounds(bounds);
      })
      .catch(() => {
        if (isMounted) setMapError(true);
      });

    return () => {
      isMounted = false;
      markers.forEach((marker) => marker.setMap(null));
      markers = [];
    };
  }, [validLocations, navigate, detailBasePath, backTo]);

  if (!locations.length) return null;

  /* Modified by: Ishan | Soft-contrast nearest screen locations card */
  return (
    <section className="mt-5 mb-4 overflow-hidden rounded-[24px] border border-[#EEEBF8] bg-white p-[22px] shadow-[0_10px_30px_rgba(60,40,120,0.05)]">
      <div className="mb-4 text-left sm:mb-5">
        <h2 className="text-[18px] font-extrabold tracking-tight text-[#3A3660]">
          Nearest screen locations
        </h2>
        <p className="mt-0.5 text-[13px] text-[#6A6688]">Nearby screens on the map</p>
      </div>
      {mapError ? (
        <div className="flex h-[280px] w-full items-center justify-center rounded-[20px] bg-[#F3F2F9] text-sm font-medium text-[#6A6688] sm:h-[360px]">
          Map unavailable. Please check your API key.
        </div>
      ) : (
        <div className="relative h-[280px] w-full overflow-hidden rounded-[20px] border border-[#EEEBF8] sm:h-[360px] lg:h-[400px]">
          <div ref={mapRef} className="h-full w-full" />
        </div>
      )}
    </section>
  );
}
